import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { CardState, CardData } from '../types/cardTypes';
import { CardStateService } from '../services/cardStateService';
import { ApiService } from '../services/apiService';

interface CardContextType {
  cardData: CardData | null;
  cardId: string | null;
  isLoading: boolean;
  error: string | null;
  setCardData: (data: CardData | null) => void;
  setCardId: (id: string) => void;
  redirectToCorrectPage: () => void;
  fetchCardData: (cardId: string) => Promise<void>;
}

const CardContext = createContext<CardContextType | undefined>(undefined);

interface CardProviderProps {
  children: ReactNode;
}

export const CardProvider: React.FC<CardProviderProps> = ({ children }) => {
  const [cardData, setCardData] = useState<CardData | null>(null);
  const [cardId, setCardId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  
  const navigate = useNavigate();



  const fetchCardData = useCallback(async (id: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await ApiService.getCardByCardId(id);
      
      if (response) {
        setCardData(response);
      } else {
        const errorMessage = `No card found for ID: ${id}`;
        setError(errorMessage);
        navigate(CardStateService.getErrorRoute(errorMessage, id));
      }
    } catch (error) {
      const errorMessage = `Error fetching card: ${id}`;
      setError(errorMessage);
      navigate(CardStateService.getErrorRoute(errorMessage, id));
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  // Remove automatic URL parameter handling to prevent infinite loops
  // Let individual components handle their own data fetching

  const redirectToCorrectPage = () => {
    if (!cardData || !cardId) return;

    const targetRoute = CardStateService.getRouteForCardState(cardData.state, cardId);
    navigate(targetRoute);
  };

  const value: CardContextType = {
    cardData,
    cardId,
    isLoading,
    error,
    setCardData,
    setCardId,
    redirectToCorrectPage,
    fetchCardData,
  };

  return (
    <CardContext.Provider value={value}>
      {children}
    </CardContext.Provider>
  );
};

export const useCardContext = (): CardContextType => {
  const context = useContext(CardContext);
  if (context === undefined) {
    throw new Error('useCardContext must be used within a CardProvider');
  }
  return context;
};

 